// ─────────────────────────────────────────────────────────────────────────────
// theme.js  —  GuntingJer Design Tokens
//
// ONE file controls the entire app's visual identity.
// Change a value here → it updates everywhere instantly.
//
// Import examples:
//   import { colors, typography, radius, glassCard, btnPrimary } from '../../theme';
//   import { colors } from '../theme';   ← for src-level files
// ─────────────────────────────────────────────────────────────────────────────

// ══════════════════════════════════════════════════════════════════════════════
// 1. THEME PRESET SELECTOR
//    Change the value below to switch the entire app theme instantly.
// ══════════════════════════════════════════════════════════════════════════════

const generateSecondary = (hex) => {
    if (!hex.startsWith('#')) return hex;

    const num = parseInt(hex.slice(1), 16);
    let r = (num >> 16) & 255;
    let g = (num >> 8) & 255;
    let b = num & 255;

    // 🔥 Smart adjustments
    r = Math.min(255, r + 20);
    g = Math.min(255, g + 10);
    b = Math.min(255, b + 30);

    const newHex =
        '#' +
        [r, g, b]
            .map((x) => x.toString(16).padStart(2, '0'))
            .join('');

    return newHex;
};


const PRESET = 'newCustom';   // ← change this to switch themes

//  Options:
//  'pearlGlass'   — soft white glass on periwinkle blue  (iOS-style)
//  'obsidian'     — dark charcoal with gold accents      (premium dark)
//  'sakura'       — blush pink glass on warm white       (soft & elegant)
//  'midnight'     — deep navy glass with cyan accents    (professional dark)
//  'forest'       — green glass on sage                  (calm, natural)
//  'slate'        — cool grey glass, monochrome          (minimal, corporate)
//  'neoBlue'      — crisp blue glass                     (modern, electric)
//  'neonAI'       — violet glass                         (AI / tech)
//  'mintGlass'    — teal/mint glass                      (fresh, health)
//  'sunsetGlass'  — warm orange glass                    (energetic)
//  'luxuryLight'  — near-black on white                  (luxury minimal)
//  'custom'       — your own custom palette              (edit below)


const PRESETS = {
 
    pearlGlass: {
        // ── Periwinkle base with blue/indigo blobs ──
        bg:         '#c8d3ee',
        bgGradient: `
            radial-gradient(ellipse at 15% 15%, rgba(100,130,255,0.35) 0%, transparent 55%),
            radial-gradient(ellipse at 85% 80%, rgba(160,100,255,0.25) 0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, rgba(79,140,255,0.15) 0%, transparent 60%),
            #c8d3ee`,
        bgCard:       'rgba(255,255,255,0.52)',
        bgDeep:       'rgba(255,255,255,0.32)',
        bgTopBar:     'rgba(255,255,255,0.70)',
        border:       'rgba(255,255,255,0.38)',
        borderLight:  'rgba(255,255,255,0.60)',
        primary:      '#4F8CFF',
        primaryDark:  '#2a64e0',
        cardBlur:     'blur(28px) saturate(180%)',
        modalBlur:    'blur(40px) saturate(200%)',
        thinBlur:     'blur(16px) saturate(160%)',
        shadow:       'rgba(31,45,90,0.10)',
        shadowStrong: 'rgba(31,45,90,0.18)',
        inset:        'rgba(255,255,255,0.80)',
        insetBottom:  'rgba(31,45,90,0.06)',
        selectedBg:   'rgba(79,140,255,0.22)',
    },
 
    obsidian: {
        // ── Deep charcoal with warm gold/amber glow ──
        bg:         '#0f0f13',
        bgGradient: `
            radial-gradient(ellipse at 20% 20%, rgba(212,168,67,0.18) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 70%, rgba(180,80,20,0.15)  0%, transparent 50%),
            radial-gradient(ellipse at 50% 90%, rgba(100,50,200,0.10) 0%, transparent 40%),
            #0f0f13`,
        bgCard:       'rgba(30,28,36,0.85)',
        bgDeep:       'rgba(20,18,26,0.90)',
        bgTopBar:     'rgba(15,15,19,0.92)',
        border:       'rgba(255,255,255,0.08)',
        borderLight:  'rgba(255,255,255,0.15)',
        primary:      '#d4a843',
        primaryDark:  '#b8902f',
        cardBlur:     'blur(20px) saturate(120%)',
        modalBlur:    'blur(32px) saturate(140%)',
        thinBlur:     'blur(12px)',
        shadow:       'rgba(0,0,0,0.40)',
        shadowStrong: 'rgba(0,0,0,0.60)',
        inset:        'rgba(255,255,255,0.04)',
        insetBottom:  'rgba(0,0,0,0.20)',
        selectedBg:   'rgba(212,168,67,0.22)',
    },
 
    sakura: {
        // ── Blush pink with rose/coral blobs ──
        bg:         '#f5e8e8',
        bgGradient: `
            radial-gradient(ellipse at 20% 10%, rgba(255,160,180,0.40) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 85%, rgba(255,100,140,0.25) 0%, transparent 50%),
            radial-gradient(ellipse at 55% 45%, rgba(255,200,210,0.30) 0%, transparent 55%),
            #f5e8e8`,
        bgCard:       'rgba(255,250,250,0.60)',
        bgDeep:       'rgba(255,240,240,0.40)',
        bgTopBar:     'rgba(255,252,252,0.78)',
        border:       'rgba(255,200,200,0.35)',
        borderLight:  'rgba(255,220,220,0.55)',
        primary:      '#e85d8a',
        primaryDark:  '#c4426f',
        cardBlur:     'blur(24px) saturate(160%)',
        modalBlur:    'blur(36px) saturate(180%)',
        thinBlur:     'blur(14px) saturate(140%)',
        shadow:       'rgba(180,60,100,0.08)',
        shadowStrong: 'rgba(180,60,100,0.16)',
        inset:        'rgba(255,255,255,0.85)',
        insetBottom:  'rgba(180,60,100,0.04)',
        selectedBg:   'rgba(232,93,138,0.20)',
    },
 
    midnight: {
        // ── Deep navy with cyan/teal blobs ──
        bg:         '#070d1a',
        bgGradient: `
            radial-gradient(ellipse at 10% 20%, rgba(0,100,200,0.30)   0%, transparent 50%),
            radial-gradient(ellipse at 90% 80%, rgba(0,200,200,0.20)   0%, transparent 50%),
            radial-gradient(ellipse at 50% 10%, rgba(50,100,255,0.20)  0%, transparent 45%),
            #070d1a`,
        bgCard:       'rgba(10,18,38,0.80)',
        bgDeep:       'rgba(6,12,28,0.88)',
        bgTopBar:     'rgba(8,14,30,0.90)',
        border:       'rgba(100,160,255,0.12)',
        borderLight:  'rgba(100,160,255,0.22)',
        primary:      '#38bdf8',
        primaryDark:  '#0ea5e9',
        cardBlur:     'blur(24px) saturate(140%)',
        modalBlur:    'blur(36px) saturate(160%)',
        thinBlur:     'blur(14px)',
        shadow:       'rgba(0,0,0,0.50)',
        shadowStrong: 'rgba(0,0,0,0.70)',
        inset:        'rgba(100,160,255,0.06)',
        insetBottom:  'rgba(0,0,0,0.30)',
        selectedBg:   'rgba(56,189,248,0.22)',
    },
 
    forest: {
        // ── Sage green with emerald/lime blobs ──
        bg:         '#d4e4d0',
        bgGradient: `
            radial-gradient(ellipse at 15% 20%, rgba(50,180,80,0.30)  0%, transparent 50%),
            radial-gradient(ellipse at 85% 80%, rgba(20,120,60,0.25)  0%, transparent 50%),
            radial-gradient(ellipse at 55% 10%, rgba(140,220,100,0.20) 0%, transparent 45%),
            #d4e4d0`,
        bgCard:       'rgba(240,250,238,0.55)',
        bgDeep:       'rgba(225,242,220,0.38)',
        bgTopBar:     'rgba(245,252,243,0.72)',
        border:       'rgba(255,255,255,0.42)',
        borderLight:  'rgba(255,255,255,0.62)',
        primary:      '#2d8a4e',
        primaryDark:  '#1e6638',
        cardBlur:     'blur(24px) saturate(160%)',
        modalBlur:    'blur(36px) saturate(180%)',
        thinBlur:     'blur(14px) saturate(140%)',
        shadow:       'rgba(20,60,30,0.10)',
        shadowStrong: 'rgba(20,60,30,0.18)',
        inset:        'rgba(255,255,255,0.80)',
        insetBottom:  'rgba(20,60,30,0.05)',
        selectedBg:   'rgba(45,138,78,0.22)',
    },
 
    slate: {
        // ── Cool grey with subtle blue/indigo blobs ──
        bg:         '#dde2ec',
        bgGradient: `
            radial-gradient(ellipse at 20% 20%, rgba(120,140,200,0.25) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(80,100,180,0.18)  0%, transparent 50%),
            radial-gradient(ellipse at 50% 50%, rgba(100,120,220,0.12) 0%, transparent 55%),
            #dde2ec`,
        bgCard:       'rgba(255,255,255,0.50)',
        bgDeep:       'rgba(240,242,248,0.38)',
        bgTopBar:     'rgba(255,255,255,0.75)',
        border:       'rgba(180,188,210,0.35)',
        borderLight:  'rgba(200,208,228,0.55)',
        primary:      '#475569',
        primaryDark:  '#334155',
        cardBlur:     'blur(20px) saturate(120%)',
        modalBlur:    'blur(32px) saturate(140%)',
        thinBlur:     'blur(12px)',
        shadow:       'rgba(40,50,80,0.10)',
        shadowStrong: 'rgba(40,50,80,0.18)',
        inset:        'rgba(255,255,255,0.80)',
        insetBottom:  'rgba(40,50,80,0.05)',
        selectedBg:   'rgba(71,85,105,0.22)',
    },
 
    neoBlue: {
        // ── Electric blue with deep blue/cyan blobs ──
        bg:         '#eaf1ff',
        bgGradient: `
            radial-gradient(ellipse at 15% 15%, rgba(79,140,255,0.35)  0%, transparent 50%),
            radial-gradient(ellipse at 85% 80%, rgba(0,180,255,0.22)   0%, transparent 50%),
            radial-gradient(ellipse at 60% 5%,  rgba(120,80,255,0.18)  0%, transparent 40%),
            #eaf1ff`,
        bgCard:       'rgba(255,255,255,0.55)',
        bgDeep:       'rgba(255,255,255,0.35)',
        bgTopBar:     'rgba(255,255,255,0.75)',
        border:       'rgba(255,255,255,0.40)',
        borderLight:  'rgba(255,255,255,0.60)',
        primary:      '#4F8CFF',
        primaryDark:  '#2f6fe0',
        cardBlur:     'blur(28px) saturate(180%)',
        modalBlur:    'blur(40px) saturate(200%)',
        thinBlur:     'blur(16px)',
        shadow:       'rgba(79,140,255,0.15)',
        shadowStrong: 'rgba(79,140,255,0.25)',
        inset:        'rgba(255,255,255,0.85)',
        insetBottom:  'rgba(0,0,0,0.05)',
        selectedBg:   'rgba(79,140,255,0.22)',
    },
 
    neonAI: {
        // ── Violet/indigo with pink and blue blobs ──
        bg:         '#eef0ff',
        bgGradient: `
            radial-gradient(ellipse at 10% 15%, rgba(123,97,255,0.35)  0%, transparent 50%),
            radial-gradient(ellipse at 90% 85%, rgba(180,80,255,0.25)  0%, transparent 50%),
            radial-gradient(ellipse at 55% 5%,  rgba(79,140,255,0.22)  0%, transparent 45%),
            radial-gradient(ellipse at 30% 80%, rgba(220,100,255,0.18) 0%, transparent 40%),
            #eef0ff`,
        bgCard:       'rgba(255,255,255,0.50)',
        bgDeep:       'rgba(255,255,255,0.30)',
        bgTopBar:     'rgba(255,255,255,0.70)',
        border:       'rgba(255,255,255,0.35)',
        borderLight:  'rgba(255,255,255,0.55)',
        primary:      '#7B61FF',
        primaryDark:  '#5a45d6',
        cardBlur:     'blur(30px) saturate(200%)',
        modalBlur:    'blur(45px) saturate(220%)',
        thinBlur:     'blur(18px)',
        shadow:       'rgba(123,97,255,0.15)',
        shadowStrong: 'rgba(123,97,255,0.25)',
        inset:        'rgba(255,255,255,0.90)',
        insetBottom:  'rgba(0,0,0,0.05)',
        selectedBg:   'rgba(123,97,255,0.22)',
    },
 
    mintGlass: {
        // ── Mint/teal with aqua and green blobs ──
        bg:         '#e8fffb',
        bgGradient: `
            radial-gradient(ellipse at 15% 15%, rgba(46,211,183,0.35)  0%, transparent 50%),
            radial-gradient(ellipse at 85% 80%, rgba(0,200,160,0.25)   0%, transparent 50%),
            radial-gradient(ellipse at 55% 5%,  rgba(80,220,200,0.20)  0%, transparent 40%),
            #e8fffb`,
        bgCard:       'rgba(255,255,255,0.60)',
        bgDeep:       'rgba(255,255,255,0.40)',
        bgTopBar:     'rgba(255,255,255,0.75)',
        border:       'rgba(255,255,255,0.40)',
        borderLight:  'rgba(255,255,255,0.60)',
        primary:      '#2ED3B7',
        primaryDark:  '#14b8a6',
        cardBlur:     'blur(26px) saturate(180%)',
        modalBlur:    'blur(38px) saturate(200%)',
        thinBlur:     'blur(14px)',
        shadow:       'rgba(20,184,166,0.15)',
        shadowStrong: 'rgba(20,184,166,0.25)',
        inset:        'rgba(255,255,255,0.85)',
        insetBottom:  'rgba(0,0,0,0.05)',
        selectedBg:   'rgba(46,211,183,0.25)',
    },
 
    sunsetGlass: {
        // ── Warm orange/coral with amber and red blobs ──
        bg:         '#fff1eb',
        bgGradient: `
            radial-gradient(ellipse at 15% 15%, rgba(255,122,24,0.35)  0%, transparent 50%),
            radial-gradient(ellipse at 85% 80%, rgba(255,60,80,0.20)   0%, transparent 50%),
            radial-gradient(ellipse at 55% 5%,  rgba(255,180,50,0.25)  0%, transparent 45%),
            #fff1eb`,
        bgCard:       'rgba(255,255,255,0.55)',
        bgDeep:       'rgba(255,255,255,0.35)',
        bgTopBar:     'rgba(255,255,255,0.70)',
        border:       'rgba(255,255,255,0.35)',
        borderLight:  'rgba(255,255,255,0.55)',
        primary:      '#ff7a18',
        primaryDark:  '#e85d0f',
        cardBlur:     'blur(28px) saturate(180%)',
        modalBlur:    'blur(40px) saturate(200%)',
        thinBlur:     'blur(16px)',
        shadow:       'rgba(255,122,24,0.15)',
        shadowStrong: 'rgba(255,122,24,0.25)',
        inset:        'rgba(255,255,255,0.85)',
        insetBottom:  'rgba(0,0,0,0.05)',
        selectedBg:   'rgba(255,122,24,0.22)',
    },
 
    luxuryLight: {
        // ── Near-white with very subtle warm/cool blobs ──
        bg:         '#f8f7f4',
        bgGradient: `
            radial-gradient(ellipse at 20% 20%, rgba(200,180,140,0.20) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(180,160,120,0.15) 0%, transparent 50%),
            radial-gradient(ellipse at 55% 5%,  rgba(220,210,190,0.18) 0%, transparent 45%),
            #f8f7f4`,
        bgCard:       'rgba(255,255,255,0.65)',
        bgDeep:       'rgba(255,255,255,0.45)',
        bgTopBar:     'rgba(255,255,255,0.80)',
        border:       'rgba(0,0,0,0.08)',
        borderLight:  'rgba(0,0,0,0.12)',
        primary:      '#111111',
        primaryDark:  '#000000',
        cardBlur:     'blur(20px) saturate(120%)',
        modalBlur:    'blur(30px) saturate(140%)',
        thinBlur:     'blur(12px)',
        shadow:       'rgba(0,0,0,0.08)',
        shadowStrong: 'rgba(0,0,0,0.15)',
        inset:        'rgba(255,255,255,0.90)',
        insetBottom:  'rgba(0,0,0,0.04)',
        selectedBg:   'rgba(0,0,0,0.10)',
    },
 
    // ── Custom ────────────────────────────────────────────────────────────────
    custom: {
        bg:         'rgb(247,241,236)',
        bgGradient: `
            radial-gradient(ellipse at 15% 15%, rgba(200,120,40,0.28)  0%, transparent 50%),
            radial-gradient(ellipse at 85% 80%, rgba(180,80,20,0.20)   0%, transparent 50%),
            radial-gradient(ellipse at 55% 5%,  rgba(220,160,80,0.22)  0%, transparent 45%),
            rgb(247,241,236)`,
        bgCard:       'rgba(255,255,255,0.55)',
        bgDeep:       'rgba(255,255,255,0.35)',
        bgTopBar:     'rgba(255,255,255,0.75)',
        border:       'rgba(133,69,10,0.15)',
        borderLight:  'rgba(133,69,10,0.25)',
        primary:      '#85450a',
        primaryDark:  '#6b3608',
        cardBlur:     'blur(28px) saturate(180%)',
        modalBlur:    'blur(40px) saturate(200%)',
        thinBlur:     'blur(16px) saturate(160%)',
        shadow:       'rgba(133,69,10,0.12)',
        shadowStrong: 'rgba(133,69,10,0.22)',
        inset:        'rgba(255,255,255,0.80)',
        insetBottom:  'rgba(133,69,10,0.05)',
        selectedBg:   'rgba(133,69,10,0.22)',
    },

newCustom: {
    // ── Warm barbershop: dark brown · golden yellow · off-white ──
    bg:         '#e7e6eb',
    bgGradient: `rgb(255, 255, 255)`,
    bgCard:       'rgba(255,255,255,0.65)',
    bgDeep:       'rgba(255,255,255,0.40)',
    bgTopBar:     'rgba(231,230,235,0.82)',
    border:       'rgba(50,41,32,0.12)',
    borderLight:  'rgba(50,41,32,0.20)',
    primary:      '#eccb4d',          // golden yellow
    primaryDark:  '#d4a820',          // darker gold hover
    cardBlur:     'blur(28px) saturate(180%)',
    modalBlur:    'blur(40px) saturate(200%)',
    thinBlur:     'blur(16px) saturate(160%)',
    shadow:       'rgba(50,41,32,0.10)',
    shadowStrong: 'rgba(50,41,32,0.20)',
    inset:        'rgba(255,255,255,0.80)',
    insetBottom:  'rgba(50,41,32,0.04)',
    selectedBg:   'rgba(236,203,77,0.28)',
},
};
 
// ── Active preset ─────────────────────────────────────────────────────────────
const p = PRESETS[PRESET] ?? PRESETS.pearlGlass;
 
const DARK_PRESETS = new Set(['obsidian', 'midnight']);
const isDark = DARK_PRESETS.has(PRESET);
 
const secondaryColor = generateSecondary(p.primary);

// ══════════════════════════════════════════════════════════════════════════════
// 3. COLOUR TOKENS
// ══════════════════════════════════════════════════════════════════════════════

export const colors = {

    // ── Backgrounds ──────────────────────────────────────────────────────────
    bg:       p.bg,        // page background  ← also update index.css
    bgCard:   p.bgCard,    // glass card surface
    bgDeep:   p.bgDeep,    // inset / inputs / inner chips
    bgTopBar: p.bgTopBar,  // sticky nav / app bar
    bgGradient :p.bgGradient,

    // ── Borders ──────────────────────────────────────────────────────────────
    border:      p.border,
    borderLight: p.borderLight,

    // ── Primary ──────────────────────────────────────────────────────────────
    primary:       p.primary,
    primaryHover:  p.primaryDark,
    primaryDeep:   p.primaryDark,
    primaryDim:    `${p.primary}1e`,   // ~12% opacity
    primaryBorder: `${p.primary}59`,   // ~35% opacity

    // ── Secondary — violet ────────────────────────────────────────────────────
    secondary:       secondaryColor,
    secondaryHover:  p.primaryDark,
    secondaryDim:    `${secondaryColor}1a`,
    secondaryBorder: `${secondaryColor}4d`,

    // ── Text ──────────────────────────────────────────────────────────────────
textPrimary:  isDark ? '#f0f0f8' : '#322920',   // dark brown ✅ already set
textMuted:    isDark ? '#9898b8' : '#888481',    // warm grey ✅ already set
textFaint:    isDark ? '#6a6a88' : '#888481',    // warm grey
textFaintest: isDark ? '#4a4a68' : '#a5514f',   // terracotta for faint hints
textDisabled: isDark ? '#38384e' : '#c8c5c0',   // lighter grey

    // ── Semantic — success ────────────────────────────────────────────────────
    success:       '#22c55e',
    successBg:     'rgba(34,197,94,0.12)',
    successBorder: 'rgba(34,197,94,0.30)',

    // ── Semantic — warning ────────────────────────────────────────────────────
    warning:       '#f59e0b',
    warningBg:     'rgba(245,158,11,0.12)',
    warningBorder: 'rgba(245,158,11,0.30)',

    // ── Semantic — error ──────────────────────────────────────────────────────
    error:       '#a5514f',
    errorBg:     'rgba(248,113,113,0.12)',
    errorBorder: 'rgba(248,113,113,0.30)',
    errorDeep:   '#3e0808',

    // ── Semantic — info ───────────────────────────────────────────────────────
    info:       '#818cf8',
    infoBg:     'rgba(129,140,248,0.12)',
    infoBorder: 'rgba(129,140,248,0.30)',

    // ── Semantic — teal ───────────────────────────────────────────────────────
    teal:       '#2dd4bf',
    tealBg:     'rgba(45,212,191,0.12)',
    tealBorder: 'rgba(45,212,191,0.30)',

    // ── Plan tiers ────────────────────────────────────────────────────────────
    planFree:         '#8c96b8',
    planBasic:        '#2dd4bf',
    planPro:          p.primary,
    planMaster:       '#e2a0ff',
    planMasterBorder: 'rgba(226,160,255,0.33)',
    planMasterBg:     'rgba(26,10,46,0.60)',

    // ── Utility ───────────────────────────────────────────────────────────────
    white: '#ffffff',
    black: '#000000',

    // ── Selection state ───────────────────────────────────────────────────────
    // Change selectedBg in your preset (above) to change the selected card colour
    selectedBackground: p.selectedBg ?? `${p.primary}26`,
    selectedBorder:     p.primary,
};


// ══════════════════════════════════════════════════════════════════════════════
// 4. BLUR VALUES  (exported so components don't hardcode blur strings)
// ══════════════════════════════════════════════════════════════════════════════

export const cardBlur  = p.cardBlur;   // standard card blur — changes with preset
export const modalBlur = p.modalBlur;  // modal / bottom sheet blur
export const thinBlur  = p.thinBlur;   // nav bar / thin strip blur


// ══════════════════════════════════════════════════════════════════════════════
// 5. GLASS SURFACE PRESETS
//    Use as sx spread: <Box sx={{ ...glassCard }}>
// ══════════════════════════════════════════════════════════════════════════════

export const glassCard = {
    background:           p.bgCard,
    backdropFilter:       p.cardBlur,
    WebkitBackdropFilter: p.cardBlur,
    border:               `1px solid ${p.primary}`,
    boxShadow:            `0 4px 24px ${p.shadow}, 0 1px 0 ${p.inset} inset, 0 -1px 0 ${p.insetBottom} inset`,
};

export const glassModal = {
    background:           p.bgCard.replace(/[\d.]+\)$/, m => String(Math.min(parseFloat(m) + 0.15, 1)) + ')'),
    backdropFilter:       p.modalBlur,
    WebkitBackdropFilter: p.modalBlur,
    border:               `1px solid ${p.borderLight}`,
    boxShadow:            `0 8px 40px ${p.shadowStrong}, 0 1px 0 ${p.inset} inset`,
    borderRadius:         24,
};

export const glassThin = {
    background:           p.bgTopBar,
    backdropFilter:       p.thinBlur,
    WebkitBackdropFilter: p.thinBlur,
    border:               `1px solid ${p.border}`,
};

export const chipStyle = {
    background:           p.bgDeep,
    backdropFilter:       'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius:         999,
    px: 1.2,
    py: 0.3,
    border:               `1px solid ${p.borderLight}`,
};


// ══════════════════════════════════════════════════════════════════════════════
// 6. TYPOGRAPHY
// ══════════════════════════════════════════════════════════════════════════════

export const typography = {
    serif:     "'Playfair Display', serif",
    sansSerif: "'Inter', 'Roboto', sans-serif",
    mono:      'monospace',
    poppins: "'Poppins', sans-serif"
};


// ══════════════════════════════════════════════════════════════════════════════
// 7. BORDER RADIUS
// ══════════════════════════════════════════════════════════════════════════════

export const radius = {
    xs:   0.5,
    sm:   1,
    md:   2,
    lg:   3,
    xl:   4,
    xxl:  5,
    full: '50%',
    pill: 999,
};


// ══════════════════════════════════════════════════════════════════════════════
// 8. SHADOWS
// ══════════════════════════════════════════════════════════════════════════════

export const shadows = {
    card:        `0 4px 24px ${p.shadow}`,
    cardHover:   `0 8px 32px ${p.shadowStrong}`,
    modal:       `0 8px 40px ${p.shadowStrong}`,
    float:       `0 2px 12px ${p.shadow}`,
    glow:        '0 0 8px  rgba(34,197,94,0.50)',
    glowPrimary: `0 0 12px ${p.primary}66`,
    glowError:   '0 0 8px  rgba(248,113,113,0.40)',
    glowWarning: '0 0 8px  rgba(245,158,11,0.40)',
};


// ══════════════════════════════════════════════════════════════════════════════
// 9. SPACING
// ══════════════════════════════════════════════════════════════════════════════

export const space = {
    xs:  0.5,
    sm:  1,
    md:  2,
    lg:  3,
    xl:  4,
    xxl: 6,
};


// ══════════════════════════════════════════════════════════════════════════════
// 10. MUI sx OBJECTS
// ══════════════════════════════════════════════════════════════════════════════

export const inputSx = {
    '& .MuiOutlinedInput-root': {
        background:           p.primary,
        backdropFilter:       'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius:         12,
        color:                colors.textPrimary,
        fontSize:             13,
        '& fieldset':             { borderColor: colors.textPrimary },
        '&:hover fieldset':       { borderColor: colors.primary },
        '&.Mui-focused fieldset': { borderColor: colors.primary, borderWidth: 1.5 },
    },
    '& input':    { color: colors.textPrimary },
    '& textarea': { color: colors.textPrimary },
    '& .MuiInputLabel-root':             { color: colors.textPrimary,  fontSize: 12 },
    '& .MuiInputLabel-root.Mui-focused': { color: colors.primary },
    '& .MuiFormHelperText-root':         { color: colors.error, fontSize: 11 },
};

export const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked':                    { color: colors.success },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.success },
};

export const switchPrimarySx = {
    '& .MuiSwitch-switchBase.Mui-checked':                    { color: colors.primary },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.primaryBorder },
};

export const btnPrimary = {
    background:    p.primary,
    color:         '#322920',   // dark brown on yellow — NOT white
    borderRadius:  12,
    border:        `1px solid ${p.primaryDark}`,
    fontWeight:    700,
    textTransform: 'none',
    boxShadow:     `0 4px 20px ${p.primary}59`,
    '&:hover': { background: p.primaryDark, boxShadow: `0 6px 24px ${p.primary}80` },
    '&:disabled': { background: p.bgDeep, color: '#c8c5c0', border: `1px solid ${p.border}`, boxShadow: 'none' },
};

export const btnSecondary = {
    background:           `${colors.secondary}cc`,
    backdropFilter:       'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    color:                colors.white,
    borderRadius:         12,
    border:               `1px solid ${colors.secondaryBorder}`,
    fontWeight:           700,
    textTransform:        'none',
    boxShadow:            `0 4px 20px ${colors.secondaryBorder}`,
    '&:hover': {
        background: colors.secondary,
        boxShadow:  `0 6px 24px ${colors.secondaryBorder}`,
    },
};

export const btnOutlined = {
    background:           p.bgDeep,
    backdropFilter:       'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border:               `1px solid ${colors.primaryBorder}`,
    color:                colors.primary,
    fontWeight:           600,
    textTransform:        'none',
    borderRadius:         12,
    '&:hover': {
        background:  colors.primaryDim,
        borderColor: colors.primary,
    },
};

export const btnSuccess = {
    background:    colors.successBg,
    border:        `1px solid ${colors.successBorder}`,
    color:         colors.success,
    fontWeight:    700,
    textTransform: 'none',
    borderRadius:  12,
    '&:hover': { background: 'rgba(34,197,94,0.22)' },
};

export const btnDanger = {
    background:    colors.errorBg,
    border:        `1px solid ${colors.errorBorder}`,
    color:         colors.error,
    fontWeight:    700,
    textTransform: 'none',
    borderRadius:  12,
    '&:hover': { background: 'rgba(248,113,113,0.22)' },
};

export const btnWarning = {
    background:    colors.warningBg,
    border:        `1px solid ${colors.warningBorder}`,
    color:         colors.warning,
    fontWeight:    700,
    textTransform: 'none',
    borderRadius:  12,
    '&:hover': { background: 'rgba(245,158,11,0.22)' },
};

export const btnGhost = {
    background:    'transparent',
    border:        'none',
    color:         colors.textMuted,
    fontWeight:    600,
    textTransform: 'none',
    borderRadius:  12,
    '&:hover': { background: colors.bgDeep, color: colors.primary },
};


// ══════════════════════════════════════════════════════════════════════════════
// 11. STATUS COLOURS
// ══════════════════════════════════════════════════════════════════════════════

export const statusColors = {
    waiting:   { bg: colors.warningBg, border: colors.warningBorder, text: colors.warning },
    serving:   { bg: colors.successBg, border: colors.successBorder, text: colors.success },
    completed: { bg: colors.infoBg,    border: colors.infoBorder,    text: colors.info    },
    cancelled: { bg: colors.errorBg,   border: colors.errorBorder,   text: colors.error   },
    pending:   { bg: colors.warningBg, border: colors.warningBorder, text: colors.warning },
};


// ══════════════════════════════════════════════════════════════════════════════
// 12. ANIMATION
// ══════════════════════════════════════════════════════════════════════════════

export const duration = {
    fast:   '0.12s',
    normal: '0.20s',
    slow:   '0.35s',
};

export const easing = {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    enter:    'cubic-bezier(0.0, 0, 0.2, 1)',
    exit:     'cubic-bezier(0.4, 0, 1, 1)',
    spring:   'cubic-bezier(0.34, 1.56, 0.64, 1)',
};


// ─────────────────────────────────────────────────────────────────────────────
// HOW TO USE
// ─────────────────────────────────────────────────────────────────────────────
//
//  SWITCH THEME:
//    const PRESET = 'obsidian';   ← line 16
//
//  CHANGE SELECTED CARD COLOUR:
//    selectedBg: '#f5d5b0',       ← inside your preset definition
//
//  IMPORT:
//    import { colors, typography, radius, glassCard, cardBlur, btnPrimary } from '../../theme';
//
//  COMMON PATTERNS:
//    bgcolor: colors.bg                              page background
//    sx={{ ...glassCard }}                           glass card
//    background: active ? colors.selectedBackground  selected card
//    backdropFilter: active ? 'none' : cardBlur      disable blur when selected
//    color: colors.primary                           accent colour
//    border: `1px solid ${colors.border}`            card border
//    fontFamily: typography.serif                    Playfair Display
//    borderRadius: radius.lg                         12px
//    sx={inputSx}                                    frosted input
//    sx={{ ...btnPrimary }}                          primary button
//
//  THREE places to update when changing bg colour:
//    1. bg value inside your preset (this file)
//    2. index.css   body { background-color: ... }
//    3. index.js    pulls from colors.bg automatically ✅
// ─────────────────────────────────────────────────────────────────────────────